import type { ReactNode } from 'react';
import { Bell, Globe, Store, Tag, type LucideIcon } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';
import type { SlideProps } from '@/lib/whats-new/edition-types';
import { useEstreiaFacts } from '@/components/whats-new/editions/estreia/use-estreia-facts';
import { useSteps } from '@/components/whats-new/editions/estreia/use-steps';
import {
  appVM,
  type AppRowId,
} from '@/components/whats-new/editions/estreia/view-models';
import { Eyebrow } from '@/components/whats-new/slides/eyebrow';
import { GlassCard } from '@/components/whats-new/slides/glass-card';
import { LineSkeleton } from '@/components/whats-new/slides/line-skeleton';
import { Rise } from '@/components/whats-new/slides/rise';
import { TextReserve } from '@/components/whats-new/slides/text-reserve';
import {
  SlideLayout,
  SlideLead,
  SlideTitle,
} from '@/components/whats-new/slides/slide-layout';
import { WordReveal } from '@/components/whats-new/slides/word-reveal';

/** The switches reach their real state one by one, 700 ms apart (NEWS-34). */
export const APP_FIRST_SWITCH_MS = 1300;
export const APP_SWITCH_STAGGER_MS = 700;
const STEPS: readonly number[] = [0, 1, 2].map(
  (i) => APP_FIRST_SWITCH_MS + i * APP_SWITCH_STAGGER_MS
);

const ICONS: Record<AppRowId, LucideIcon> = {
  coupon: Tag,
  welcome: Bell,
  site: Globe,
};

// Arbitrary properties: Tailwind's arbitrary duration and easing classes
// clash with tailwindcss-animate's and generate nothing.
const FADE =
  'transition-[opacity,background-color] [transition-duration:500ms]';
const KNOB =
  'transition-transform [transition-duration:450ms] [transition-timing-function:cubic-bezier(0.34,1.56,0.64,1)]';
/** A row's detail line and the coupon's contrast pill; their loading
    stand-ins keep the same boxes (NEWS-26). */
const DETAIL = 'text-[12.5px]';
const CONTRAST_PILL =
  'mt-1.5 inline-flex h-5 items-center rounded-full px-2 text-[10.5px] font-bold uppercase tracking-[0.08em]';

/** The site's detail holds its known room, loading and loaded (FX8c). */
function MaybeReserve({
  longest,
  children,
}: {
  longest: string | null;
  children: ReactNode;
}) {
  return longest === null ? (
    children
  ) : (
    <TextReserve longest={longest} className={DETAIL}>
      {children}
    </TextReserve>
  );
}

/**
 * 7 · Seu app (NEWS-34): the app's card (icon, name, "Publicado") and its
 * three features (coupon banner, welcome notification, website banner),
 * whose switches light up in turn up to their real state, with the real
 * setting (or "Ainda não configurado") and, on a live coupon banner, the
 * text contrast seal "x,y:1 · Legível/Baixo".
 */
export function AppSlide(_props: SlideProps) {
  const { LL, locale } = useI18nContext();
  const { store, rows } = appVM(useEstreiaFacts(), locale);
  const step = useSteps(STEPS);

  return (
    <SlideLayout
      copy={
        <>
          <Rise index={0}>
            <Eyebrow lead={LL.whatsNew.editions.estreia.app.eyebrow()} />
          </Rise>
          <SlideTitle>
            <WordReveal text={LL.whatsNew.editions.estreia.app.title()} />
          </SlideTitle>
          <Rise index={2}>
            <SlideLead>
              {LL.whatsNew.editions.estreia.app.lead({ store: store.name })}
            </SlideLead>
          </Rise>
        </>
      }
      visual={
        <Rise index={1}>
          <GlassCard className="flex flex-col gap-2.5 p-[18px] max-md:p-3.5">
            <div className="flex items-center gap-3 border-b border-white/[.12] pb-3">
              {store.iconUrl ? (
                <img
                  src={store.iconUrl}
                  alt=""
                  className="size-10 flex-none rounded-[10px]"
                />
              ) : (
                <span className="grid size-10 flex-none place-items-center rounded-[10px] bg-white/[.08] text-[#CFE0FF]">
                  <Store
                    aria-hidden="true"
                    className="size-5"
                    strokeWidth={1.6}
                  />
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-[15px] font-semibold">
                  {store.name}
                </p>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/[.86]">
                  <i className="size-1.5 rounded-full bg-[#6FA0FF]" />
                  {LL.whatsNew.editions.estreia.app.published()}
                </span>
              </div>
            </div>

            {rows.map((row, i) => {
              const Icon = ICONS[row.id];
              const state =
                row.state.state === 'ready' ? row.state.value : null;
              const reached = step > i;
              const on = reached && (state?.on ?? false);
              return (
                <div
                  key={row.id}
                  data-testid="app-row"
                  data-row={row.id}
                  data-on={on || undefined}
                  className={cn(
                    'grid grid-cols-[38px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl p-3 max-md:p-2.5',
                    FADE,
                    on ? 'bg-[rgba(111,160,255,0.12)]' : 'bg-white/[.04]'
                  )}
                >
                  <span className="grid size-[38px] place-items-center rounded-[10px] bg-white/[.08] text-[#CFE0FF]">
                    <Icon
                      aria-hidden="true"
                      className="size-[18px]"
                      strokeWidth={1.6}
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{row.title(LL)}</p>
                    <MaybeReserve longest={row.reserve?.(LL) ?? null}>
                      {state ? (
                        <p
                          data-testid="app-row-detail"
                          className={cn(DETAIL, 'text-white/[.86]')}
                        >
                          {state.detail(LL)}
                        </p>
                      ) : (
                        <>
                          <div className={DETAIL}>
                            <LineSkeleton
                              data-testid="app-row-loading"
                              className="h-3.5 w-40 bg-white/[.12]"
                            />
                          </div>
                          {/* The coupon's contrast pill, so its row keeps
                              its height when it arrives (NEWS-26). */}
                          {row.id === 'coupon' && (
                            <span
                              aria-hidden="true"
                              className={cn(CONTRAST_PILL, 'invisible')}
                            >
                              0
                            </span>
                          )}
                        </>
                      )}
                    </MaybeReserve>
                    {state?.contrast && (
                      <span
                        data-testid="app-contrast"
                        data-legible={state.contrast.legible || undefined}
                        className={cn(
                          CONTRAST_PILL,
                          'bg-white/10 text-[#CFE0FF]',
                          FADE,
                          '[transition-delay:400ms]',
                          on ? 'opacity-100' : 'opacity-0'
                        )}
                      >
                        {state.contrast.text(LL)}
                      </span>
                    )}
                  </div>
                  <span
                    data-testid="app-switch"
                    aria-hidden="true"
                    className={cn(
                      'relative h-[26px] w-[42px] rounded-full transition-colors [transition-duration:350ms]',
                      on ? 'bg-[#155DFC]' : 'bg-white/[.18]'
                    )}
                  >
                    <i
                      className={cn(
                        'absolute left-[3px] top-[3px] size-5 rounded-full bg-white shadow-[0_2px_6px_rgba(0,0,0,0.3)]',
                        KNOB,
                        on && 'translate-x-4'
                      )}
                    />
                  </span>
                </div>
              );
            })}
          </GlassCard>
        </Rise>
      }
    />
  );
}
